import styled, { css } from "styled-components";
import { Link } from "react-router-dom";

import {
  PremierPlate,
  RankArrow,
  RankDelta,
  RankIcon,
} from "MVVM/Views/Match/styledComponents";
import { DropdownMenu } from "MVVM/Components/DropdownMenu";

export const Card = styled.div<{ $promoted?: boolean; $banner?: string }>`
  background: ${({ theme }) => theme.colors.navy600};
  border: 1px solid transparent;
  border-radius: 12px;
  overflow: hidden;
  padding: 16px;

  ${({ $promoted, $banner, theme }) =>
    $promoted &&
    css`
      border-color: rgba(113, 170, 255, 0.38);
      position: relative;

      /* The competition's banner behind the card head, scrimmed to the card's
         own background so the title and byline stay legible over any image.
         Sits under the content rather than beside it — it's atmosphere, not
         something to read. */
      &::before {
        background-color: ${theme.colors.navy600};
        background-image: linear-gradient(
            180deg,
            rgba(19, 29, 37, 0.58) 0%,
            rgba(19, 29, 37, 0.86) 55%,
            ${theme.colors.navy600} 100%
          )
          ${$banner ? `, url(${$banner})` : ""};
        background-position: center;
        background-size: cover;
        content: "";
        height: 200px;
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
        z-index: 0;
      }

      > * {
        position: relative;
        z-index: 1;
      }
    `}

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    padding: 12px;
  }
`;

export const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const HeaderLine = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HeaderMeta = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ClipAvatarLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
`;

export const ClipUsernameLink = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

/* A button rather than a div: it navigates to the clip, same as the
   thumbnail below it, so it needs to be reachable by keyboard. */
export const ClipTitle = styled.button`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: -webkit-box;
  font-family: inherit;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 14px;
  overflow: hidden;
  padding: 0;
  text-align: left;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 18px;
  }
`;

/* Block-level and full width on purpose: ClipBadges measures this container
   and drops any badge that doesn't fit, so giving it the whole card width is
   what lets all four render. */
/* Says why this clip is in front of you, before anything else on the card.
   Distinct from the tag badges further down, which describe what happened
   inside the clip. */
/* Pinned is the viewer's own doing and private to them, so it reads quieter
   than the promoted treatment: a chalk row rather than a branded colour. */
export const PinnedRow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 11.5px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
  text-transform: uppercase;

  svg {
    flex-shrink: 0;
  }
`;

export const PinButton = styled.button<{ $pinned?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 6px;
  color: ${({ theme, $pinned }) =>
    $pinned ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  margin-left: auto;
  padding: 6px;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme }) => theme.colors.ice};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/* Competition cards are one big Link with an absolutely positioned overlay,
   so the control floats above the banner instead of sitting in a header row.
   Opposite corner from the status badge. */
export const CompetitionPinButton = styled.button<{ $pinned?: boolean }>`
  align-items: center;
  background: rgba(8, 12, 16, 0.6);
  backdrop-filter: blur(3px);
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 999px;
  color: ${({ theme, $pinned }) =>
    $pinned ? theme.colors.ice : theme.colors.chalk};
  cursor: pointer;
  display: flex;
  padding: 7px;
  position: absolute;
  right: 14px;
  top: 14px;
  z-index: 3;

  &:hover {
    background: rgba(8, 12, 16, 0.85);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const PinError = styled.div`
  color: ${({ theme }) => theme.colors.rare};
  font-size: 12.5px;
  margin-bottom: 10px;
`;

export const PromotedEyebrow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.blue100};
  display: flex;
  font-size: 15px;
  font-weight: 800;
  gap: 7px;
  letter-spacing: 0.01em;
  margin-bottom: 10px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`;

/* The actual point of promoting the clip: having watched it, the viewer gets
   a route into the competition it came from. Whole row is the link. */
export const PromotedCta = styled(Link)`
  align-items: center;
  background: rgba(113, 170, 255, 0.1);
  border: 1px solid rgba(113, 170, 255, 0.34);
  border-radius: 10px;
  display: flex;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 14px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(113, 170, 255, 0.18);
  }
`;

export const PromotedCtaBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const PromotedCtaTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14.5px;
  font-weight: 800;
`;

export const PromotedCtaMeta = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  flex-wrap: wrap;
  font-size: 12.5px;
  gap: 4px 8px;
`;

export const PromotedCtaPrize = styled.span`
  color: ${({ theme }) => theme.colors.blue100};
  font-weight: 700;
`;

export const PromotedCtaAction = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.blue100};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.darkerNavy};
  display: flex;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 800;
  gap: 6px;
  margin-left: auto;
  padding: 9px 14px;
  white-space: nowrap;
`;

export const TagsRow = styled.div`
  margin-bottom: 12px;
`;

export const ThumbnailWrapper = styled.div`
  background: ${({ theme }) => theme.colors.midnight};
  border-radius: 9px;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:hover .feed-item-play {
    opacity: 1;
  }
`;

export const Thumbnail = styled.img`
  aspect-ratio: 16 / 9;
  display: block;
  object-fit: cover;
  width: 100%;
`;

// Always mounted (not conditional on $active) so the opacity transition has
// something to animate from — the thumbnail underneath stays visible while
// this fades in, masking the black frame the <video> shows before its first
// real frame decodes. Same technique as Homepage/ContentItem's hover preview.
export const VideoOverlay = styled.div<{ $active: boolean }>`
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  position: absolute;
  transition: opacity 0.4s ease-in-out;
  z-index: 1;
`;

export const MuteToggle = styled.button`
  align-items: center;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  border-radius: 50%;
  bottom: 10px;
  color: #fff;
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  left: 10px;
  position: absolute;
  transition: background 0.15s ease;
  width: 30px;
  z-index: 2;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`;

export const StatOverlay = styled.div`
  align-items: flex-end;
  bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 70%;
  position: absolute;
  right: 10px;
  z-index: 3;
`;

/* Decorative only — the card wrapper owns the click. Without pointer-events
   none it covers the whole thumbnail and eats the stat bar's buttons on any
   card that isn't the autoplaying one. */
export const PlayOverlay = styled.div`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: 0.85;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.15s ease;
  z-index: 2;
`;

// display:none can make some browsers deprioritize/skip loading a video
// element entirely, so this stays "visible" at 1x1px and invisible via
// opacity instead — position:absolute keeps it out of the card's layout.
export const PreloadVideo = styled.video`
  height: 1px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  width: 1px;
`;

/* right, not just left: ClipBadges truncates to its container's measured
   offsetWidth, and an absolutely positioned box with only `left` set
   shrink-wraps to ~0, so every badge got dropped as "doesn't fit". */
export const CompetitionCard = styled(Link)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.premiumPurpleAlpha50};
  border-radius: 12px;
  display: block;
  overflow: hidden;
  text-decoration: none;

  &:hover {
    box-shadow: 0 12px 30px -18px rgba(0, 0, 0, 0.7);
  }
`;

// The whole card is this box now — banner, scrim, and every piece of copy
// stack as layers inside it instead of copy living in a separate panel below
// a small image. min-height (not aspect-ratio) because the content stack's
// height genuinely varies by state — upcoming has less to show than a
// completed competition with a full winners row — an aspect-ratio box would
// either clip the fuller states or waste space on the sparser ones.
export const CompetitionMedia = styled.div`
  background: ${({ theme }) => theme.colors.midnight};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 420px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    min-height: 340px;
  }
`;

// Background-image div rather than an <img> — matches how the competition
// detail page's own hero (HeroBg) renders its banner, and sizes more
// predictably against a min-height (vs. aspect-ratio) container.
export const CompetitionBanner = styled.div<{ $src?: string }>`
  background: ${({ $src }) =>
    $src ? `url(${$src}) center/cover no-repeat` : "none"};
  inset: 0;
  position: absolute;
`;

// Bottom-anchored gradient, heavier and taller than a simple card scrim since
// the content stack now runs title through winners — same purpose as the
// competition detail page's own HeroScrim, reshaped for a bottom-loaded
// vertical card instead of a wide side-lit hero.
export const CompetitionScrim = styled.div`
  background: linear-gradient(
    to top,
    rgba(6, 10, 14, 0.98) 0%,
    rgba(6, 10, 14, 0.92) 45%,
    rgba(6, 10, 14, 0.55) 75%,
    rgba(6, 10, 14, 0.1) 100%
  );
  inset: 0;
  position: absolute;
`;

type TCompetitionBadgeVariant = "live" | "upcoming" | "ended";

export const CompetitionStatusBadge = styled.span<{
  $variant: TCompetitionBadgeVariant;
}>`
  align-items: center;
  background: ${({ $variant, theme }) =>
    $variant === "live"
      ? theme.colors.turbo
      : $variant === "upcoming"
      ? theme.colors.goldStar
      : "rgba(0, 0, 0, 0.6)"};
  border-radius: 999px;
  color: ${({ $variant, theme }) =>
    $variant === "ended" ? theme.colors.chalk : theme.colors.darkerNavy};
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  left: 16px;
  letter-spacing: 0.05em;
  padding: 6px 12px;
  position: absolute;
  text-transform: uppercase;
  top: 16px;
  z-index: 2;
`;

export const LiveDot = styled.span`
  background: currentColor;
  border-radius: 50%;
  height: 6px;
  width: 6px;
  animation: activity-feed-live-pulse 1.4s ease-in-out infinite;

  @keyframes activity-feed-live-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }
`;

/* In flow, not absolute. Absolute meant this stack contributed no height at
   all, so CompetitionMedia's min-height was the card's only height and a
   content-heavy state — an ended competition with ended row, prize, chips and
   a winners podium — grew upward straight through the top edge and got sliced
   off by CompetitionCard's overflow: hidden. In flow, the card grows to fit;
   CompetitionMedia's justify-content: flex-end keeps sparser states pinned to
   the bottom exactly as before. Banner and scrim stay absolute behind it. */
export const CompetitionOverlay = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  position: relative;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    gap: 10px;
    padding: 14px;
  }
`;

export const CompetitionTitle = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.08;
  overflow-wrap: anywhere;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 20px;
  }
`;

export const CompetitionByline = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const CompetitionCreatorLink = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 7px;
  padding: 0;

  &:hover span {
    text-decoration: underline;
  }
`;

export const CompetitionCreatorLabel = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12.5px;
  opacity: 0.75;
`;

export const CompetitionCreatorName = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12.5px;
  font-weight: 700;
`;

export const CompetitionOfficialTag = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.turbo};
  display: inline-flex;
  font-size: 12.5px;
  font-weight: 700;
  gap: 5px;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`;

export const CompetitionMetaDivider = styled.span`
  background: rgba(255, 255, 255, 0.25);
  height: 12px;
  width: 1px;
`;

export const CompetitionMetaCount = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 12.5px;
  font-weight: 600;
  gap: 6px;
  opacity: 0.85;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  b {
    font-weight: 800;
    opacity: 1;
  }
`;

export const CompetitionPrizeBlock = styled.div``;

export const CompetitionPrizeLabel = styled.div`
  color: ${({ theme }) => theme.colors.goldStar};
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const CompetitionPrizeValue = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 2px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);
`;

// Frosted glass box, same construction as the detail page's countdown strip
// (CountdownWrapper) — a distinct "widget" rather than another line of text.
// align-self + width keep this hugging its own content — without it, the box
// stretches to CompetitionOverlay's full card width (unlike the detail page,
// where the equivalent widget sits inside a fixed 320px aside column), which
// spread the four digit cells way out since each is flex: 1 of that width.
export const CompetitionCountdownBox = styled.div`
  align-self: flex-start;
  background: rgba(12, 19, 24, 0.6);
  backdrop-filter: blur(10px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  padding: 10px 14px;
  width: min(230px, 100%);
`;

export const CompetitionCountdownLabel = styled.div`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const CompetitionCountdownDigits = styled.div`
  align-items: baseline;
  display: flex;
  gap: 4px;
  justify-content: space-between;
  margin-top: 6px;
`;

export const CompetitionCountdownCell = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

export const CompetitionCountdownNum = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
`;

export const CompetitionCountdownUnit = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-top: 4px;
  opacity: 0.7;
  text-transform: uppercase;
`;

export const CompetitionCountdownSep = styled.div`
  color: rgba(255, 255, 255, 0.35);
  font-size: 18px;
  font-weight: 300;
  line-height: 1;
`;

export const CompetitionEndedRow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
  justify-content: flex-start;
  padding: 4px 0;

  svg {
    color: ${({ theme }) => theme.colors.caution};
  }
`;

export const CompetitionChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const CompetitionChip = styled.span`
  align-items: center;
  background: rgba(8, 12, 16, 0.55);
  backdrop-filter: blur(2px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 5px;
  padding: 3px 8px;

  svg {
    color: ${({ theme }) => theme.colors.ice};
    flex-shrink: 0;
    opacity: 0.85;
  }
`;

// Wraps WinnersHeading + WinnersFeatureRow — the extra top padding gives the
// section room to breathe below the rule chips instead of sitting flush
// against them.
export const WinnersSection = styled.div`
  padding-bottom: 14px;
  padding-top: 14px;
`;

export const WinnersHeading = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.goldStar};
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  justify-content: center;
  letter-spacing: 0.1em;
  padding-bottom: 10px;
  text-transform: uppercase;

  svg {
    flex-shrink: 0;
  }
`;

// Podium arrangement: 2nd — 1st — 3rd, 1st place rendered larger via
// $featured and bottom-aligned so its extra height reads as "elevated"
// rather than the row just being uneven.
export const WinnersFeatureRow = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 18px;
  justify-content: center;
`;

// Not a Link: this renders inside CompetitionCard, which is itself a Link —
// nested <a> tags are invalid HTML and break click behavior, so navigation
// here is handled with useHistory instead.
export const WinnerFeatureItem = styled.div<{ $featured?: boolean }>`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: ${({ $featured }) => ($featured ? 76 : 60)}px;

  &:hover img {
    transform: scale(1.05);
  }
`;

export const WinnerAvatarRing = styled.div<{
  $color?: string;
  $featured?: boolean;
}>`
  border: ${({ $featured }) => ($featured ? 3 : 2)}px solid
    ${({ $color, theme }) => $color ?? theme.colors.chalk};
  border-radius: 50%;
  box-shadow: ${({ $featured }) =>
    $featured ? "0 0 14px rgba(255, 215, 0, 0.3)" : "none"};
  height: ${({ $featured }) => ($featured ? 68 : 48)}px;
  padding: 2px;
  position: relative;
  width: ${({ $featured }) => ($featured ? 68 : 48)}px;
`;

export const WinnerFeatureAvatar = styled.img`
  border-radius: 50%;
  display: block;
  height: 100%;
  object-fit: cover;
  transition: transform 0.15s ease;
  width: 100%;
`;

export const WinnerRankBadge = styled.span<{
  $color?: string;
  $featured?: boolean;
}>`
  align-items: center;
  background: ${({ $color, theme }) => $color ?? theme.colors.chalk};
  border: 2px solid ${({ theme }) => theme.colors.darkerNed};
  border-radius: 50%;
  bottom: -4px;
  color: ${({ theme }) => theme.colors.darkerNavy};
  display: flex;
  font-size: ${({ $featured }) => ($featured ? 10 : 9)}px;
  font-weight: 800;
  height: ${({ $featured }) => ($featured ? 19 : 17)}px;
  justify-content: center;
  position: absolute;
  right: -4px;
  width: ${({ $featured }) => ($featured ? 19 : 17)}px;
`;

export const WinnerFeatureName = styled.span<{ $featured?: boolean }>`
  color: ${({ theme }) => theme.colors.ice};
  font-size: ${({ $featured }) => ($featured ? 12.5 : 11)}px;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const WinnerFeatureScore = styled.span<{
  $color?: string;
  $featured?: boolean;
}>`
  color: ${({ $color, theme }) => $color ?? theme.colors.ice};
  font-size: ${({ $featured }) => ($featured ? 12 : 10.5)}px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  opacity: 0.9;
`;

// Still small/plain next to the Clip and Competition cards' full hero
// treatment — a "by the way, new post" teaser, just with a bit more visual
// interest than flat text. overflow:hidden clips BlogBackgroundAvatar to the
// card's rounded corners.
export const BlogCard = styled(Link)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.turboAlpha50};
  border-radius: 12px;
  display: block;
  overflow: hidden;
  padding: 16px 18px;
  position: relative;
  text-decoration: none;

  &:hover {
    box-shadow: 0 12px 30px -18px rgba(0, 0, 0, 0.7);
  }
`;

// Blog posts have no thumbnail field of their own (see homepage.ts) — the
// author's avatar, blown up and faded into the card via mask-image, is the
// only visual asset available. Sits behind BlogContent (z-index 0 vs. 1).
export const BlogBackgroundAvatar = styled.img`
  -webkit-mask-image: linear-gradient(to left, black 25%, transparent 100%);
  height: 100%;
  mask-image: linear-gradient(to left, black 25%, transparent 100%);
  object-fit: cover;
  opacity: 0.5;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  width: 55%;
  z-index: 0;
`;

export const BlogContent = styled.div`
  max-width: 65%;
  position: relative;
  z-index: 1;
`;

export const BlogTag = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.turbo};
  display: inline-flex;
  font-size: 24px;
  font-weight: 800;
  gap: 9px;
  letter-spacing: 0.03em;
  margin-bottom: 12px;
  text-transform: uppercase;
`;

export const BlogTitle = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ theme }) => theme.colors.ice};
  display: -webkit-box;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.3;
  overflow: hidden;
`;

export const BlogByline = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 12.5px;
  gap: 6px;
  margin-top: 10px;

  > *:not(:last-child)::after {
    content: "\\00b7";
    margin-left: 6px;
  }
`;

export const BlogAuthorGroup = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 5px;
`;

export const BlogAvatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 20px;
  object-fit: cover;
  width: 20px;
`;

export const BlogAuthorName = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 600;
`;

const resultColor = (result: string | null | undefined) =>
  result === "Win" ? "lime" : result === "Loss" ? "caution" : "chalk";

const matchFocus = css`
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

const matchLabel = css`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const MatchHeader = styled.header`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

export const MatchMenu = styled(DropdownMenu)`
  align-self: flex-start;
  flex-shrink: 0;
`;

export const MatchMenuButton = styled.button<{ $copied: boolean }>`
  align-items: center;
  background: none;
  border: none;
  border-radius: 999px;
  color: ${({ theme, $copied }) =>
    $copied ? theme.colors.envy : theme.colors.chalk};
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  padding: 0;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  width: 36px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme, $copied }) =>
      $copied ? theme.colors.envy : theme.colors.ice};
  }

  ${matchFocus}
`;

export const MatchMapTile = styled.div<{ $art?: string | null }>`
  align-items: center;
  align-self: stretch;
  background-color: ${({ theme }) => theme.colors.navy500};
  background-image: ${({ $art }) => ($art ? `url(${$art})` : "none")};
  background-position: center;
  background-size: cover;
  border-radius: 6px;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  min-height: 56px;
  overflow: hidden;
  position: relative;
  width: 40px;

  &::before {
    background: rgba(0, 0, 0, 0.45);
    content: "";
    inset: 0;
    position: absolute;
  }

  img {
    height: 28px;
    object-fit: contain;
    position: relative;
    width: 28px;
  }
`;

export const MatchHeading = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 2px;
  min-width: 0;
`;

export const MatchTitle = styled.h2`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 18px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0;
  min-width: 0;

  strong {
    color: ${({ theme }) => theme.colors.ice};
    font-weight: 700;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    font-size: 16px;
  }
`;

export const MatchTime = styled.time`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
`;

const matchPanel = css`
  background: ${({ theme }) => theme.colors.navy500};
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

export const MatchArt = styled.img<{ $clipFrame?: boolean }>`
  height: 100%;
  inset: 0;
  object-fit: cover;
  object-position: ${({ $clipFrame }) =>
    $clipFrame ? "center 85%" : "center"};
  position: absolute;
  width: 100%;
`;

export const MatchArtScrim = styled.div`
  background: linear-gradient(
    0deg,
    rgba(12, 19, 24, 0.95) 10%,
    rgba(12, 19, 24, 0.55) 55%,
    rgba(12, 19, 24, 0.15) 100%
  );
  inset: 0;
  position: absolute;
`;

export const MatchMomentPanel = styled.div`
  ${matchPanel}
  align-items: flex-end;
  box-sizing: border-box;
  color: inherit;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
  min-height: 260px;
  padding: 20px;
  text-decoration: none;

  &[href]:hover > span:last-child {
    background: ${({ theme }) => theme.colors.darkerEnvy};
    transform: scale(1.06);
  }

  ${matchFocus}

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
  }
`;

export const MatchMomentBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  position: relative;
`;

export const MatchWeapon = styled.img`
  height: auto;
  width: 104px;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    width: 84px;
  }
`;

export const MatchMomentTitle = styled.p`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
`;

export const MatchMomentMeta = styled.p`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  margin: 0;
`;

export const MatchMomentError = styled.p`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 14px;
  margin: 0;
`;

export const MatchMomentAction = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  padding: 12px 22px;
  position: relative;
  text-decoration: none;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
    color: ${({ theme }) => theme.colors.ice};
  }

  ${matchFocus}
`;

export const MatchPlay = styled.span`
  align-items: center;
  align-self: flex-end;
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  display: flex;
  flex-shrink: 0;
  height: 56px;
  justify-content: center;
  position: relative;
  transition:
    background 0.15s ease,
    transform 0.15s ease;
  width: 56px;

  svg {
    margin-left: 3px;
  }
`;

export const MatchMomentStatus = styled.span`
  ${matchLabel}
  background: ${({ theme }) => theme.colors.midnightAlpha80};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  padding: 10px 16px;
  position: relative;
  text-align: center;
`;

export const MatchOutcome = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 2px;
`;

export const MatchScore = styled.span<{ $result?: string | null }>`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 28px;
  font-style: italic;
  font-variant-numeric: tabular-nums;
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1;

  b {
    color: ${({ theme, $result }) => theme.colors[resultColor($result)]};
    font-weight: inherit;
  }

  span {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
    margin: 0 6px;
  }
`;

export const MatchResult = styled.span<{ $result?: string | null }>`
  ${matchLabel}
  color: ${({ theme, $result }) => theme.colors[resultColor($result)]};
`;

export const MatchStats = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const statLabel = css`
  dt {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

export const MatchStat = styled.div<{ $color: string }>`
  ${statLabel}

  dd {
    color: ${({ theme, $color }) => theme.colors[$color]};
    font-size: 20px;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    line-height: 24px;
    margin: 2px 0 0;
  }
`;

export const MatchRank = styled.div`
  ${statLabel}

  dd {
    align-items: center;
    display: flex;
    height: 24px;
    margin: 2px 0 0;
  }

  ${RankIcon} {
    height: 24px;
  }

  ${PremierPlate} em {
    font-size: 16px;
  }

  ${RankDelta} {
    font-size: 16px;
  }

  ${RankArrow} {
    font-size: 18px;
  }
`;

export const MatchLeaderboard = styled.section`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  table {
    border-collapse: collapse;
    font-size: 18px;
    width: 100%;
  }

  th,
  td {
    padding: 8px 10px;
    text-align: right;
    white-space: nowrap;
  }

  thead th:not(:first-child),
  td {
    width: 80px;
  }

  ${RankIcon} {
    height: 20px;
  }

  ${PremierPlate} em {
    font-size: 13px;
  }

  thead th {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding-bottom: 4px;
    text-transform: uppercase;
  }

  th:first-child {
    max-width: 0;
    text-align: left;
    width: 100%;
  }

  th[scope="colgroup"] {
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    max-width: none;
    padding-top: 12px;
    text-transform: uppercase;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
      display: none;
    }
  }
`;

export const MatchLeaderboardRow = styled.tr<{ $viewer: boolean }>`
  color: ${({ theme, $viewer }) =>
    $viewer ? theme.colors.ice : theme.colors.iceAlpha75};
  font-variant-numeric: tabular-nums;

  td,
  th {
    background: ${({ theme, $viewer }) =>
      $viewer ? theme.colors.navy100 : "transparent"};
  }

  > :first-child {
    border-radius: 6px 0 0 6px;
  }

  > :last-child {
    border-radius: 0 6px 6px 0;
  }

  &:hover td,
  &:hover th {
    background: ${({ theme, $viewer }) =>
      $viewer ? theme.colors.navy100 : theme.colors.navy500};
  }

  th[scope="row"] {
    font-weight: ${({ $viewer }) => ($viewer ? 700 : 500)};
  }

  a {
    align-items: center;
    border-radius: 4px;
    color: inherit;
    display: flex;
    gap: 12px;
    min-width: 0;
    text-decoration: none;

    [data-text] {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    [data-line] {
      align-items: center;
      display: flex;
      gap: 6px;
      max-width: 100%;
    }

    [data-name] {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:hover [data-name] {
      color: ${({ theme }) => theme.colors.ice};
      text-decoration: underline;
    }

    ${matchFocus}
  }
`;

export const MatchLobbyToggle = styled.button`
  background: none;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  margin-top: 4px;
  padding: 5px 9px;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  ${matchFocus}
`;

export const MatchLeaderboardRating = styled.span<{ $color: string }>`
  color: ${({ theme, $color }) => theme.colors[$color]};
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

export const MatchStatsRow = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  justify-content: space-between;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const MatchActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    margin-left: 0;
    width: 100%;

    > * {
      flex: 1;
      text-align: center;
    }
  }
`;

const matchAction = css`
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  gap: 6px;
  justify-content: center;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
  padding: 10px 20px;
  text-decoration: none;
  transition: background 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  ${matchFocus}
`;

export const MatchSecondaryAction = styled(Link)`
  ${matchAction}
  background: ${({ theme }) => theme.colors.navy};

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
  }
`;

export const MatchPrimaryAction = styled(Link)`
  ${matchAction}
  background: ${({ theme }) => theme.colors.envy};

  &:hover {
    background: ${({ theme }) => theme.colors.darkerEnvy};
  }
`;
